import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Collapse,
  Container,
  Stack,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Basket from "../components/Basket";
import { useSettings } from "../contexts/SettingsContext";
import { Bundle, db, type Product } from "../database/db";
import { BasketData, BasketItem } from "../utils/basket_helpers";

async function findMatchingBundles(items: BasketItem[]): Promise<Bundle[]> {
  const availableBundles = await db.bundles.toArray();
  const sortedBundles = [...availableBundles].sort(
    (a, b) => b.discountAmount - a.discountAmount
  );

  const remainingItems = [...items];
  const matchedBundles: Bundle[] = [];

  for (const bundle of sortedBundles) {
    const requirements = JSON.parse(bundle.requirements);

    let canMatchBundle = true;
    while (canMatchBundle) {
      canMatchBundle = requirements.every(
        (req: { requiredProductId: number; count: number }) => {
          const availableItem = remainingItems.find(
            (item) =>
              item.id === req.requiredProductId && item.quantity >= req.count
          );
          return availableItem !== undefined;
        }
      );

      if (canMatchBundle) {
        requirements.forEach(
          (req: { requiredProductId: number; count: number }) => {
            const itemIndex = remainingItems.findIndex(
              (item) => item.id === req.requiredProductId
            );
            remainingItems[itemIndex] = {
              ...remainingItems[itemIndex],
              quantity: remainingItems[itemIndex].quantity - req.count,
            };
          }
        );
        matchedBundles.push(bundle);
      }
    }
  }

  return matchedBundles;
}

export default function ShopPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [basketData, setBasketData] = useState<BasketData>({
    items: [],
    bundles: [],
    adjustedTotal: null,
    notes: null,
  });
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    return () => {
      products.forEach((product) => {
        if (product.imageBlob) {
          URL.revokeObjectURL(URL.createObjectURL(product.imageBlob));
        }
      });
    };
  }, [products]);

  const showSnackbar = (message: string) => {
    setMessage(message);
    setOpen(true);
  };

  const loadProducts = async () => {
    const allProducts = await db.products.toArray();
    setProducts(allProducts);
  };

  const updateBasketData = useCallback(
    async (
      newDataOrUpdater: BasketData | ((prev: BasketData) => BasketData),
      regenerateBundles: boolean = true
    ) => {
      const newData =
        typeof newDataOrUpdater === "function"
          ? newDataOrUpdater(basketData)
          : newDataOrUpdater;

      if (!regenerateBundles) {
        setBasketData(newData);
        return;
      }

      console.log("Regenerating bundles");
      const matchedBundles = await findMatchingBundles(newData.items);
      console.log("Matched bundles", matchedBundles);
      setBasketData({
        ...newData,
        bundles: matchedBundles,
      });
    },
    [basketData]
  );

  const handleAddToBasket = useCallback(
    (product: Product) => {
      updateBasketData((current) => {
        const items = current.items;
        let newItems = [];
        const existingItem = items.find((item) => item.id === product.id);
        if (existingItem) {
          newItems = items.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          newItems = [
            ...items,
            {
              ...product,
              quantity: 1,
            },
          ];
        }

        return {
          ...current,
          items: newItems,
        };
      });
    },
    [updateBasketData]
  );

  const handleScroll = useCallback((e: WheelEvent) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    const productsElement = document.querySelector(".shop-page-products");
    const basketElement = document.querySelector(".shop-page-basket");

    // Type assertion to HTMLElement and use correct event listener type
    const wheelHandler = (e: Event) => {
      if (e instanceof WheelEvent) {
        handleScroll(e);
      }
    };
    productsElement?.addEventListener("wheel", wheelHandler);
    basketElement?.addEventListener("wheel", wheelHandler);
  }, [handleScroll]);

  return (
    <Container maxWidth="xl" sx={{ height: "calc(100vh - 55px)" }}>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="success" onClose={() => setOpen(false)}>
          {message}
        </Alert>
      </Snackbar>
      <Stack
        direction={{ sm: "column", md: "row" }}
        spacing={2}
        sx={{ height: "100%" }}>
        <Stack
          className="shop-page-products"
          sx={{
            flex: { xs: "1", md: "2" },
            overflow: "hidden",
            display: "flex",
          }}>
          {products.length === 0 ? (
            <Box sx={{ textAlign: "center", mt: 8 }}>
              <Typography variant="h6" gutterBottom>
                No products available
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/inventory")}>
                Add Products in Inventory
              </Button>
            </Box>
          ) : (
            <Stack sx={{ height: "100%", overflow: "auto" }}>
              <Box sx={{ flex: 1 }} padding={2} paddingBottom={4}>
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {products
                    .filter((product) => product.price > 0)
                    .map((product) => (
                      <ButtonBase
                        key={product.id}
                        onClick={() => handleAddToBasket(product)}
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "calc(50% - 8px)",
                            md: "calc(33.333% - 11px)",
                          },
                        }}>
                        <Card sx={{ width: "100%", height: "100%" }}>
                          <CardContent>
                            <Box
                              sx={{
                                height: 200,
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                mb: 2,
                                bgcolor: "action.hover",
                                borderRadius: 1,
                              }}>
                              {product.imageBlob ? (
                                <img
                                  src={URL.createObjectURL(product.imageBlob)}
                                  alt={product.name}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                  }}
                                />
                              ) : (
                                <ImageNotSupportedIcon
                                  sx={{
                                    fontSize: 60,
                                    color: "text.secondary",
                                    opacity: 0.5,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="h6" component="h2">
                              {product.name}
                            </Typography>
                            <Typography
                              color={
                                product.price < 0 ? "error" : "text.secondary"
                              }>
                              {settings.currencySymbol}
                              {product.price.toFixed(settings.currencyRounding)}
                            </Typography>
                            <Typography variant="body2">
                              {product.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </ButtonBase>
                    ))}
                </Stack>

                <Box sx={{ mt: 4 }}>
                  <Button
                    onClick={() => setShowDiscounts(!showDiscounts)}
                    endIcon={
                      showDiscounts ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    sx={{ mb: 1 }}>
                    Discounts
                  </Button>
                  <Collapse in={showDiscounts}>
                    <Stack direction="row" flexWrap="wrap" gap={2}>
                      {products
                        .filter((product) => product.price < 0)
                        .map((product) => (
                          <ButtonBase
                            key={product.id}
                            sx={{
                              backgroundColor: "rgba(76, 175, 80, 0.08)",
                              width: {
                                xs: "100%",
                                sm: "calc(50% - 8px)",
                                md: "calc(33.333% - 11px)",
                              },
                            }}
                            onClick={() => handleAddToBasket(product)}>
                            <Card
                              sx={{
                                backgroundColor: "rgba(76, 175, 80, 0.08)",
                                width: "100%",
                              }}>
                              <CardContent>
                                <Typography variant="h6" component="h2">
                                  {product.name}
                                </Typography>
                                <Typography color="error">
                                  {settings.currencySymbol}
                                  {product.price.toFixed(
                                    settings.currencyRounding
                                  )}
                                </Typography>
                                <Typography variant="body2">
                                  {product.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          </ButtonBase>
                        ))}
                    </Stack>
                  </Collapse>
                </Box>
              </Box>
            </Stack>
          )}
        </Stack>
        <Stack
          className="shop-page-basket"
          sx={{
            flex: { xs: "1", md: "1" },
            height: "100%",
            overflow: "auto",
            borderRadius: 2,
            border: { sm: "1px solid #ccc", md: "none" },
          }}>
          <Basket
            basketData={basketData}
            setBasketData={updateBasketData}
            showSnackbar={showSnackbar}
          />
        </Stack>
      </Stack>
    </Container>
  );
}
